 'use client';
import React from 'react';
import { Avatar, Box, Card, useTheme } from '@mui/material';
import { differenceInDays, isBefore } from 'date-fns';
import { Icon } from '@iconify/react';

interface ConnectedAccountCardProps {
  platform: string;
  isConnected: boolean;
  username?: string;
  connectionDate?: Date;
  tokenExpiresAt?: Date;
  onConnect: (platform: string) => void;
  /** Kept for API compatibility; connected accounts cannot be unlinked, so this is never called. */
  onDisconnect?: (platform: string) => void;
}

const DEFAULT_ICON_SIZE = 20;

/** Iconify (simple-icons) icon IDs for branded platform icons. Fallback to Avatar for unknown. */
const PLATFORM_ICONIFY_IDS: Record<string, string> = {
  instagram: 'simple-icons:instagram',
  tiktok: 'simple-icons:tiktok',
  youtube: 'simple-icons:youtube',
  google: 'simple-icons:google',
  linkedin: 'simple-icons:linkedin',
  github: 'simple-icons:github',
  twitter: 'simple-icons:x',
  facebook: 'simple-icons:facebook',
  pinterest: 'simple-icons:pinterest',
  whatsapp: 'simple-icons:whatsapp',
  telegram: 'simple-icons:telegram',
  reddit: 'simple-icons:reddit',
  snapchat: 'simple-icons:snapchat',
  discord: 'simple-icons:discord',
  twitch: 'simple-icons:twitch',
  vimeo: 'simple-icons:vimeo',
  dribbble: 'simple-icons:dribbble',
  slack: 'simple-icons:slack',
  wechat: 'simple-icons:wechat',
  mastodon: 'simple-icons:mastodon',
  substack: 'simple-icons:substack',
  upstash: 'simple-icons:upstash',
};

function getPlatformIcon(platform: string, size: number = DEFAULT_ICON_SIZE) {
  const key = platform.toLowerCase();
  const iconId = PLATFORM_ICONIFY_IDS[key];
  if (iconId) {
    return <Icon icon={iconId} width={size} height={size} />;
  }
  return (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.5, bgcolor: getPlatformColor(platform) }}>
      {platform.charAt(0).toUpperCase()}
    </Avatar>
  );
}

// Helper to get platform avatar background color
const getPlatformColor = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'instagram': return '#E4405F'; case 'tiktok': return '#000000'; case 'youtube': return '#FF0000';
    case 'google': return '#DB4437'; case 'linkedin': return '#0A66C2'; case 'twitter': return '#1DA1F2';
    case 'github': return '#24292E'; case 'facebook': return '#1877F2'; case 'pinterest': return '#E60023';
    case 'whatsapp': return '#25D366'; case 'wechat': return '#07C160'; case 'telegram': return '#0088CC';
    case 'reddit': return '#FF4500'; case 'snapchat': return '#FFFC00'; case 'discord': return '#7289DA';
    case 'twitch': return '#6441A5'; case 'mastodon': return '#6364FF'; case 'substack': return '#FF671E';
    case 'vimeo': return '#1AB7EA'; case 'dribbble': return '#EA4C89'; case 'upstash': return '#7100FF';
    case 'slack': return '#4A154B';
    default: return '#cccccc';
  }
};

/** Exported for use in PlatformSelector, IntegrationsPanel, and page.tsx. Renders the icon for a given platform. */
export function PlatformIcon(props: { platform?: string; platformId?: string; name?: string; size?: number }) {
  const platformKey = props.platform ?? props.platformId ?? '';
  const size = props.size ?? 24;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {getPlatformIcon(platformKey, size)}
    </Box>
  );
}

export const ConnectedAccountCard: React.FC<ConnectedAccountCardProps> = ({
  platform,
  isConnected,
  // username,
  // connectionDate,
  tokenExpiresAt,
  onConnect,
}) => {
  const theme = useTheme();

  // Determine token status
  let tokenStatus: 'active' | 'expiring' | 'expired' | null = null;
  if (tokenExpiresAt) {
    const now = new Date();
    const expiry = tokenExpiresAt;
    const daysUntilExpiry = differenceInDays(expiry, now);

    if (isBefore(expiry, now)) {
      tokenStatus = 'expired';
    } else if (daysUntilExpiry <= 7) {
      tokenStatus = 'expiring';
    } else {
      tokenStatus = 'active';
    }
  }

  // Determine border color based on connection and token status
  let borderColor = theme.palette.grey[500]; // Default grey for disconnected
  if (isConnected) {
    if (tokenStatus === 'expired') {
      borderColor = theme.palette.error.main; // Red for expired
    } else if (tokenStatus === 'expiring') {
      borderColor = theme.palette.warning.main; // Orange for expiring soon
    } else {
      borderColor = theme.palette.success.main; // Green for active
    }
  }

  const handleClick = () => {
    if (!isConnected || tokenStatus === 'expired' || tokenStatus === 'expiring') {
      onConnect(platform);
    }
    // Connected accounts cannot be unlinked (product policy); no disconnect action.
  };

  return (
    <Card
      sx={{
        borderRadius: '50%', // Make it circular
        boxShadow: theme.shadows[3], // More pronounced shadow
        backgroundColor: theme.palette.background.paper, // Use paper background
        border: `3px solid ${borderColor}`, // Conditional border for status
        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s', // Add transition for hover effects and border color
        '&:hover': {
          transform: 'scale(1.05)', // Slight scale up on hover
          boxShadow: theme.shadows[6],
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 50, // Reduced width for story bubbles
        height: 50, // Reduced height for story bubbles
        p: 1, // Reduced padding for compactness
        cursor: 'pointer', // Indicate interactivity
        overflow: 'hidden', // Hide any overflow from scaling
      }}
      onClick={handleClick}
    >
      {getPlatformIcon(platform, DEFAULT_ICON_SIZE)}
    </Card>
  );
};
